import type { AreaReportSnapshotWithId } from './useAreaReportSnapshots';

/**
 * `AreaReportDashboardPage` の比較 (docs/25 §10-5)。選択した複数レポートの `axisCounts` を
 * 調査項目で和集合し、行 = 項目 / 列 = レポート のマトリクスに変換する。label/color は最初に
 * そのキーを持つレポートの値を採用する (レポート間でマスタ改訂により label/color がずれても
 * 表示は安定させる)。
 *
 * 2026-09-16 (オーナー指示): 比較のグラフを積み上げにするため、レポートごとの評価の内訳
 * (`polarities`) も一緒に返す (保存済みスナップショットにも内訳が入っている)。
 */
export interface ComparisonPolarity {
  positive: number;
  negative: number;
  neutral: number;
}

export interface AreaReportComparisonRow {
  key: string;
  label: string;
  color: string;
  /** `reports` と同じ順序。該当カテゴリが無いレポートは 0。 */
  values: number[];
  /**
   * `values` を各レポートの `pointCount` で正規化した「1地点あたり平均件数」(2026-07-21 追加)。
   * 地点数が多いエリアほど検出件数の絶対値も単純に増えるため、地点数の異なるエリア同士を
   * 生の件数で比較すると誤解を招く (ユーザー指摘)。`reports` と同じ順序。pointCount=0 は 0。
   */
  perPointValues: number[];
  /** レポートごとの評価の内訳 (無ければ null)。`reports` と同じ順序。 */
  polarities: (ComparisonPolarity | null)[];
}

export interface AreaReportComparisonResult {
  rows: AreaReportComparisonRow[];
}

function polarityOf(axis: { polarity?: unknown } | undefined): ComparisonPolarity | null {
  const p = axis?.polarity as Partial<ComparisonPolarity> | undefined;
  if (!p) return null;
  return {
    positive: p.positive ?? 0,
    negative: p.negative ?? 0,
    neutral: p.neutral ?? 0,
  };
}

export function buildAreaReportComparison(
  reports: AreaReportSnapshotWithId[],
): AreaReportComparisonResult {
  const order: string[] = [];
  const meta = new Map<string, { label: string; color: string }>();
  for (const report of reports) {
    for (const axis of report.axisCounts) {
      if (!meta.has(axis.key)) {
        order.push(axis.key);
        meta.set(axis.key, { label: axis.label, color: axis.color });
      }
    }
  }

  const rows: AreaReportComparisonRow[] = order.map((key) => {
    const { label, color } = meta.get(key)!;
    const found = reports.map((report) => report.axisCounts.find((axis) => axis.key === key));
    const values = found.map((axis) => axis?.value ?? 0);
    const perPointValues = values.map((v, i) => {
      const pointCount = reports[i]!.pointCount;
      return pointCount > 0 ? v / pointCount : 0;
    });
    return { key, label, color, values, perPointValues, polarities: found.map(polarityOf) };
  });

  return { rows };
}
